import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex } from "antd"
import React, { useCallback, useState } from "react"
import { Link } from "react-router-dom"

import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import { homeSize } from "../home-theme.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { CreateProjectWizard } from "../../Project/ProjectAddPage/wizard/CreateProjectWizard.tsx"
import { dashboardSetProp } from "../redux/dashboard.slice.ts"
import type { DashboardState } from "../redux/dashboard.types.ts"

export type DashboardTableFilterKey = keyof Pick<
  DashboardState,
  "leadFilters" | "projectFilters"
>

const settings: Record<
  DashboardTableFilterKey,
  {
    placeholder: string
    buttonName: string
  } & ({ href: string } | { opensProjectWizard: true })
> = {
  leadFilters: {
    placeholder: "Search leads...",
    href: RoutesPaths.lead.add,
    buttonName: "New Lead"
  },
  projectFilters: {
    placeholder: "Search projects...",
    // The same wizard the Projects page opens — `/projects/add` is the legacy
    // form, and the wizard is a modal with no route of its own.
    opensProjectWizard: true,
    buttonName: "New Project"
  }
}

interface DashboardHeaderSearchProps {
  canCreate: boolean
  tableFilterKey: DashboardTableFilterKey
}

export function DashboardHeaderSearch({
  tableFilterKey,
  canCreate
}: DashboardHeaderSearchProps) {
  const dispatch = useAppDispatch()
  const filters = useAppSelector((state) => state.dashboard?.[tableFilterKey])
  const [openProjectWizard, setOpenProjectWizard] = useState(false)

  const setting = settings[tableFilterKey]

  const handleSearch = useCallback(
    (q: string | null) => {
      dispatch(
        dashboardSetProp({ key: tableFilterKey, value: { ...filters, q } })
      )
    },
    [filters, dispatch, tableFilterKey]
  )

  const createButton = (
    <Button
      type={"primary"}
      icon={<PlusOutlined />}
      style={{
        height: homeSize.controlHeight,
        borderRadius: homeSize.controlRadius
      }}
      onClick={"href" in setting ? undefined : () => setOpenProjectWizard(true)}
    >
      {setting.buttonName}
    </Button>
  )

  return (
    <Flex gap={8} align="center" className={"max-lg:w-full"}>
      <SearchBar
        defaultValue={filters?.q || ""}
        onSearch={handleSearch}
        placeholder={setting.placeholder}
        style={{ maxWidth: 280, height: homeSize.controlHeight }}
      />

      {canCreate &&
        ("href" in setting ? (
          <Link to={setting.href}>{createButton}</Link>
        ) : (
          createButton
        ))}

      {openProjectWizard && (
        <CreateProjectWizard onClose={() => setOpenProjectWizard(false)} />
      )}
    </Flex>
  )
}
